import {
  FocusLock,
  Popover,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Switch,
} from "@chakra-ui/react";
import React from "react";
import { UsersTableBox, Form } from "./styles";
import { useSuspiciousLoginsDetailQuery } from "hooks/admin/useSuspiciousLoginsDetailQuery";
import Image from "next/image";
import { DEFAULT_AVATAR } from "helpers/constants";
import { useAppContext } from "contexts";
import VerifierProvider, { useVerifierContext } from "contexts/VerifierProvider";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { triggerNotification } from "components/TheComponents/Notification";
import { TheFieldset } from "components/TheComponents";
import CopyButton from "components/wrewards-ui-kit/Button/CopyButton";
import ChakraModal from "components/TheComponents/Modal";
import dynamic from "next/dynamic";
import { css } from "styled-components";

const ProfileLayout = dynamic(() => import("components/public/ProfileLayout"), {
  ssr: false,
});

export type SuspiciousLoginsDetailModalProps = {
  ip: string;
  onClose: () => void;
  banAllMutation?: UseMutationResult<any, any, any, any>;
  banAllReasonInputRef?: React.RefObject<HTMLTextAreaElement>;
  onBanAllSubmit?: (e: React.FormEvent<HTMLFormElement>, ip: string, otp: string) => void;
};

const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const SuspiciousLoginsDetailModal = ({
  ip,
  banAllMutation,
  banAllReasonInputRef,
  onBanAllSubmit,
}: SuspiciousLoginsDetailModalProps) => {
  const { data, isFetched, refetch } = useSuspiciousLoginsDetailQuery(ip);
  const { apiService } = useAppContext();
  const { verify, closeMfe } = useVerifierContext();
  const [popoverType, setPopoverType] = React.useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const banReasonInputRef = React.useRef<HTMLTextAreaElement>(null!);

  const oldestUser = React.useMemo(() => {
    if (!data || data.length === 0) return null;

    return data.reduce((oldest, current) => {
      const currentDate = new Date(current.createdAt).getTime();
      const oldestDate = new Date(oldest.createdAt).getTime();
      return currentDate < oldestDate ? current : oldest;
    });
  }, [data]);

  const cleanCacheForIp = async () => {
    try {
      await apiService.request(`/user-login/suspicious-logins/${ip}/clean`, {
        method: "DELETE",
      });
    } catch (error) {
      console.error("Failed to clean cache for IP:", error);
    }
  };

  const userBanMutation = useMutation({
    mutationFn: async ({
      userId,
      actionType,
      banReason,
      otp,
    }: {
      banReason?: string;
      userId: string;
      actionType: "ban" | "unban";
      otp: string;
    }) => {
      const response = await apiService.request(`/user/${userId}/${actionType}`, {
        method: "PATCH",
        headers: {
          "x-otp": otp,
        },
        ...(actionType === "ban" ? { body: JSON.stringify({ banReason }) } : {}),
      });

      const responseData = await response?.json();

      if (!response?.ok) {
        return Promise.reject(responseData);
      }

      return responseData;
    },
    onSuccess: async () => {
      await cleanCacheForIp();
      refetch?.();
    },
    onError: (error: any) =>
      triggerNotification({ text: error?.message ?? "Something went wrong", type: "error" }),
  });

  const onUserBanSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>, userId: string, otp: string) => {
      e.preventDefault();
      const banReason = new FormData(e.target as HTMLFormElement).get("banReason") as string;

      if (banReason === "" || !banReason) {
        triggerNotification({ text: "Ban Reason Required", type: "error" });
        return banReasonInputRef?.current?.focus?.();
      }

      userBanMutation.mutateAsync(
        { banReason, userId, actionType: "ban", otp },
        {
          onError: (error) =>
            triggerNotification({ text: error?.message ?? "Something went wrong", type: "error" }),
          onSuccess: () => {
            closeMfe();
            setPopoverType(null);
          },
        }
      );
    },
    [userBanMutation, closeMfe]
  );

  const onBanSwitcherChange = (userId: string, isBanned: boolean) => {
    if (isBanned) {
      verify(({ otp }) => {
        userBanMutation.mutateAsync(
          { userId, actionType: "unban", otp },
          {
            onError: (error) =>
              triggerNotification({
                text: error?.message ?? "Something went wrong",
                type: "error",
              }),
            onSuccess: () => {
              closeMfe();
              setPopoverType(null);
            },
          }
        );
      });
    } else {
      setPopoverType(userId);
    }
  };

  return (
    <UsersTableBox>
      <table>
        <thead>
          <tr>
            <td>ID</td>
            <td />
            <td>Login</td>
            <td>Display Name</td>
            <td>Email</td>
            <td>Last User Agent</td>
            <td style={{ textAlign: "center" }}>Logins Count</td>
            <td>Last Login</td>
            <td className="right">Ban</td>
          </tr>
        </thead>
        <tbody>
          {data
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((user) => {
              const isOldest = oldestUser?.userId === user.userId;

              const tdStyle = isOldest ? { backgroundColor: "#5d49334f" } : undefined;

              return (
                <tr key={user.userId} className={user.isBanned ? "isBanned" : ""}>
                  <td style={tdStyle}>{user.userId}</td>
                  <td style={tdStyle}>
                    <Image
                      src={user?.profileImageUrl ? user?.profileImageUrl : DEFAULT_AVATAR}
                      style={{ borderRadius: "50%", cursor: "pointer" }}
                      width={20}
                      height={20}
                      alt="user-avatar"
                      onClick={() => setSelectedUserId(user.userId)}
                    />
                  </td>
                  <td style={tdStyle}>{user.login}</td>
                  <td style={tdStyle}>{user.displayName}</td>
                  <td style={tdStyle}>{user.email}</td>
                  <td style={tdStyle}>
                    <div
                      style={{
                        display: "flex",
                        columnGap: "8px",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <span>{`${user.lastUserAgent.slice(0, 50)}....`}</span>
                      <CopyButton
                        copyValue={user.lastUserAgent}
                        color="gray"
                        size="sm"
                        style={{ justifyContent: "flex-start" }}
                      />
                    </div>
                  </td>
                  <td style={{ textAlign: "center", ...tdStyle }}>{user.loginsCount}</td>
                  <td style={tdStyle}>{formatDate(new Date(user.lastLoginAt))}</td>

                  <td className="right" style={tdStyle}>
                    <Popover
                      isOpen={popoverType === user.userId}
                      initialFocusRef={banReasonInputRef}
                      onOpen={() => setPopoverType(user.userId)}
                      onClose={() => setPopoverType(null)}
                      placement="left"
                      closeOnBlur={true}
                    >
                      <PopoverTrigger>
                        <Switch
                          id={`switch-ban-${user.userId}`}
                          disabled={userBanMutation.isPending}
                          isChecked={user.isBanned}
                          onChange={() => onBanSwitcherChange(user.userId, user.isBanned)}
                        />
                      </PopoverTrigger>
                      {popoverType === user.userId ? (
                        <PopoverContent p={5} pt={8} background={"#151c2c"} borderColor={"#1e2944"}>
                          <PopoverCloseButton />
                          <FocusLock>
                            <Form
                              onSubmit={(e) => {
                                e.preventDefault();
                                verify(({ otp }) => onUserBanSubmit(e, user.userId, otp));
                              }}
                            >
                              <div className="field-wrapper">
                                <TheFieldset title="Reason">
                                  <textarea
                                    ref={banReasonInputRef}
                                    placeholder={"Reason (Required)"}
                                    name="banReason"
                                  />
                                </TheFieldset>
                              </div>
                              <div>
                                <button type="submit" className="red">
                                  Ban
                                </button>
                              </div>
                            </Form>
                          </FocusLock>
                        </PopoverContent>
                      ) : null}
                    </Popover>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>

      {banAllMutation && onBanAllSubmit && (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            verify(({ otp }) => {
              onBanAllSubmit(e, ip, otp);
              closeMfe();
            });
          }}
          style={{ marginTop: "20px" }}
        >
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <TheFieldset title="Reason (Required)">
                <textarea
                  ref={banAllReasonInputRef}
                  placeholder={"Reason (Required)"}
                  name="banAllReason"
                  rows={3}
                />
              </TheFieldset>
            </div>
            <button
              type="submit"
              className="red"
              disabled={banAllMutation.isPending}
              style={{ alignSelf: "center" }}
            >
              Ban All
            </button>
          </div>
        </Form>
      )}

      {!isFetched ? (
        <div className="center">
          <Spinner />
        </div>
      ) : null}

      {selectedUserId && (
        <ChakraModal
          isOpen={!!selectedUserId}
          onClose={() => setSelectedUserId(null)}
          styles={css`
            height: calc(100dvh - 60px);
            overflow-y: scroll;
          `}
          size="6xl"
          content={<ProfileLayout userId={selectedUserId} />}
        />
      )}
    </UsersTableBox>
  );
};

const SuspiciousLoginsDetailModalWrapper = (props: SuspiciousLoginsDetailModalProps) => {
  return (
    <VerifierProvider>
      <SuspiciousLoginsDetailModal {...props} />
    </VerifierProvider>
  );
};

export default SuspiciousLoginsDetailModalWrapper;
